import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { getVisiblePageRange } from '../utility'

const PAGE_SPACING = 30

export const useVirtualization = ({ pageLayouts, scrollRef, scaleFactor }) => {
  const [pagesMetadata, setPagesMetadata] = useState([])
  const [visiblePages, setVisiblePages] = useState([])
  const [layout, setLayout] = useState({ maxWidth: 0, maxHeight: 0 })
  const pendingScrollRatioRef = useRef(null)

  useLayoutEffect(() => {
    if (!pageLayouts.length) return
    let top = 0
    let maxWidth = scrollRef.current.clientWidth

    const newPagesMetadata = pageLayouts.map((page) => {
      const { width: originalWidth, height: originalHeight, scale, type, numPages } = page
      const width = originalWidth * scaleFactor
      let height = originalHeight * scaleFactor
      // Form pages contain multiple sub-pages stacked with fixed PAGE_SPACING gaps.
      // At scale < 1, the scaled height shrinks but the gaps don't scale proportionally,
      // so we add back the unscaled portion of the inter-sub-page spacing.
      if (type === 'form') {
        const numSpacings = numPages - 1
        height += PAGE_SPACING * numSpacings * (1 - scaleFactor)
      }
      maxWidth = Math.max(maxWidth, width)
      // Gap between this page and the next, scaled by the page's effective scale.
      const spacing = PAGE_SPACING * scale * scaleFactor
      const pageLayout = { ...page, width, height, scale: scale * scaleFactor, top, bottom: top + height }
      top += height + spacing
      return pageLayout
    })

    const viewport = scrollRef.current
    const { scrollTop, scrollLeft, clientWidth, clientHeight } = viewport

    const ratioX = layout.maxWidth > 0 ? (scrollLeft + clientWidth / 2) / layout.maxWidth : 0
    const ratioY = layout.maxHeight > 0 ? (scrollTop + clientHeight / 2) / layout.maxHeight : 0

    const maxHeight = newPagesMetadata.at(-1).bottom + PAGE_SPACING

    // Capture ratios before state updates change layout
    pendingScrollRatioRef.current = { ratioX, ratioY, clientWidth, clientHeight }

    setPagesMetadata(newPagesMetadata)
    setLayout({ maxWidth, maxHeight })

    // Predict visible range at new scroll position and pre-populate
    const nextTop = maxHeight * ratioY - clientHeight / 2
    const [startIdx, endIdx] = getVisiblePageRange(nextTop, nextTop + clientHeight, newPagesMetadata)
    setVisiblePages(newPagesMetadata.slice(startIdx, endIdx + 1))
  }, [pageLayouts, scaleFactor])

  // Apply scroll correction synchronously before paint, once the new layout dimensions are committed
  useLayoutEffect(() => {
    if (!pendingScrollRatioRef.current || !scrollRef.current) return
    const { ratioX, ratioY, clientWidth, clientHeight } = pendingScrollRatioRef.current
    scrollRef.current.scrollLeft = layout.maxWidth * ratioX - clientWidth / 2
    scrollRef.current.scrollTop = layout.maxHeight * ratioY - clientHeight / 2
    pendingScrollRatioRef.current = null
  }, [layout])

  useEffect(() => {
    const scrollEl = scrollRef.current
    if (!scrollEl || pagesMetadata.length === 0) return

    const updateVisiblePages = () => {
      const viewportTop = scrollEl.scrollTop
      const viewportBottom = viewportTop + scrollEl.clientHeight
      const [startIdx, endIdx] = getVisiblePageRange(viewportTop, viewportBottom, pagesMetadata)
      setVisiblePages(pagesMetadata.slice(startIdx, endIdx + 1))
    }

    scrollEl.addEventListener('scroll', updateVisiblePages)
    updateVisiblePages()

    return () => scrollEl.removeEventListener('scroll', updateVisiblePages)
  }, [pagesMetadata, scrollRef])

  return { pagesMetadata, visiblePages, maxWidth: layout.maxWidth, maxHeight: layout.maxHeight }
}
